import { Box, Button, Paper, styled, Typography, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { COLORS } from "colors/color";
import React, { useEffect, useState } from "react";
import Chart from "react-chartjs-2";
import RefreshIcon from "@mui/icons-material/Refresh";
import applicationCandidateIcon from "../../assets/img/applicationCandidateIcon.png";
import usePostsChartData from "hooks/useApplications";
import { useTranslation } from "react-i18next";

const StyledCard = styled(Paper)(({ theme }) => ({
  borderRadius: "20px",
  overflow: "hidden",
  boxShadow: "none",
  transition: "box-shadow 0.3s ease-in-out",
  border: '1px solid #D9D9D9',
  height: '100%',
  "&:hover": {
    boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
  },
}));

const CardHeaderWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
  },
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

const CardBodyWrapper = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  height: "70%",
  width: "100%",
  position: "relative",
}));

const StyledSelect = styled(Select)(({ theme, isediting }) => ({
  height: 56,
  fontFamily: "Poppins",
  borderRadius: "10px",
  width: "100%",
  display: "flex",
  justifyContent: "end",
  alignItems: "center",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e0e0e0",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
    borderWidth: "2px",
    boxShadow: "0px 0px 5px 0.4px #0085FF",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
    borderWidth: "2px",
    boxShadow: "0px 0px 5px 0.4px #0085FF",
  },
  "& .MuiSelect-select": {
    padding: "16.5px 14px",
  },
}));


function ApplicationCadidatesChart() {
  const { postsData, loading, error } = usePostsChartData();
  const { t } = useTranslation();
  const [selectedPost, setSelectedPost] = useState('all');
  const [availablePosts, setAvailablePosts] = useState([]);

  useEffect(() => {
    if (postsData && postsData.datasets) {
      setAvailablePosts(postsData.datasets);
    }
  }, [postsData]);

  const handlePostChange = (event) => {
    setSelectedPost(event.target.value);
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,  // Set to false to completely hide the legend
      },
      tooltip: {
        backgroundColor: "#000",
        titleColor: "#fff",
        bodyColor: "#fff",
        bodyFont: {
          family: "'Inter', sans-serif",
          size: 12,
        },
        padding: 12,
        cornerRadius: 4,
        displayColors: false,
        callbacks: {
          label: (context) => `${context.raw} ${context.dataset.label}`,
        },
        responsive: true,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          padding: 8,
          color: "#64748B",
          font: {
            family: "'Inter', sans-serif",
            size: 12,
          },
          maxRotation: 45,
          minRotation: 45,
        },
        responsive: true,
      },
      y: {
        border: {
          display: false,
        },
        grid: {
          color: "#E2E8F0",
          lineWidth: 0.5,
        },
        ticks: {
          padding: 8,
          color: "#64748B",
          font: {
            family: "'Inter', sans-serif",
            size: 12,
          },
          stepSize: 100,
          callback: function(value) {
            return value >= 1000 ? (value/1000).toFixed(1) + 'k' : value;
          }
        },
        beginAtZero: true,
        responsive: true,
      },
    },
    elements: {
      bar: {
        borderRadius: 4,
        borderSkipped: false,
      },
    },
    barPercentage: 0.6,
    categoryPercentage: 0.8,
    layout: {
      padding: {
        top: 10,
        right: 10,
        bottom: 10,
        left: 10
      }
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
  };

  const filterLastSixMonths = (data) => {
    if (!data || !data.labels || !data.datasets) return null;
  
    const monthNames = [
      t("charts.applications.months.january"), 
      t("charts.applications.months.february"), 
      t("charts.applications.months.march"),
      t("charts.applications.months.april"),
      t("charts.applications.months.may"),
      t("charts.applications.months.june"),
      t("charts.applications.months.july"),
      t("charts.applications.months.august"),
      t("charts.applications.months.september"),
      t("charts.applications.months.october"),
      t("charts.applications.months.november"),
      t("charts.applications.months.december")
    ];
  
    const originalMonthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
  
    const currentMonth = new Date().getMonth();
    const last6Months = [];
    for (let i = 5; i >= 0; i--) {
      const monthIndex = (currentMonth - i + 12) % 12;
      last6Months.push(monthNames[monthIndex]);
    }
  
    // Filter data based on selected post
    let filteredDatasets = data.datasets;
    if (selectedPost !== 'all') {
      filteredDatasets = data.datasets.filter(dataset => 
        dataset.label === selectedPost
      );
    }
  
    const datasets = filteredDatasets.map(dataset => ({
      ...dataset,
      data: last6Months.map(translatedMonth => {
        const originalMonthIndex = originalMonthNames[monthNames.indexOf(translatedMonth)];
        const monthIndex = data.labels.indexOf(originalMonthIndex);
        return monthIndex !== -1 ? dataset.data[monthIndex] : 0;
      })
    }));
  
    return {
      labels: last6Months,
      datasets
    };
  };

  const filteredData = filterLastSixMonths(postsData);

  if (!filteredData) {
    const currentMonth = new Date().getMonth();
    const monthNames = [
      t("charts.applications.months.january"), 
      t("charts.applications.months.february"), 
      t("charts.applications.months.march"),
      t("charts.applications.months.april"),
      t("charts.applications.months.may"),
      t("charts.applications.months.june"),
      t("charts.applications.months.july"),
      t("charts.applications.months.august"),
      t("charts.applications.months.september"),
      t("charts.applications.months.october"),
      t("charts.applications.months.november"),
      t("charts.applications.months.december")
    ];
  
    const last6Months = [];
    for (let i = 5; i >= 0; i--) {
      const monthIndex = (currentMonth - i + 12) % 12;
      last6Months.push(monthNames[monthIndex]);
    }
  
    const defaultData = {
      labels: last6Months,
      datasets: [
        {
          label: "No Data Available",
          data: [0, 0, 0, 0, 0, 0],
          backgroundColor: "#E2E8F0",
          borderWidth: 0,
          borderRadius: 40, 
        },
      ],
    };
  
    return (
      <div className="w-full h-full">
        <Chart
          type="bar"
          data={defaultData}
          options={{
            ...chartOptions,
            maintainAspectRatio: false,
            responsive: true,
          }}
          style={{ height: "100%", width: "100%", padding: "1rem" }}
        />
      </div>
    );
  }

  return (
    <StyledCard elevation={0}>
      <CardHeaderWrapper>
        <Box
         display="flex"
         alignItems="center"
         justifyContent="space-between"
         width="100%"
        >
          <Box
            display="flex" alignItems="center"
          >
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 500,
                fontSize: { xs: '14px', sm: '16px' },
                color: '#00000080'
              }}
            >
              {/* {t("charts.applications.title")} */} Candidats de l'application
            </Typography>
          </Box>

          <FormControl size="small" sx={{
              width: "auto",
              height: { xs: 28, sm: 32 },
              borderRadius: '8px',
              borderWidth: '0.5px',
              paddingTop: { xs: '3px', sm: '5px' },
              paddingRight: { xs: '10px', sm: '15px' },
              paddingBottom: '0px',
              paddingLeft: { xs: '10px', sm: '15px' },
              gap: '8px',
            }}
          >
            {/* <InputLabel id="post-select-label">
              {t("postScoreChart.postScoreStatistics.selectPost")}
            </InputLabel> */}
            <StyledSelect
              value={selectedPost}
              onChange={handlePostChange}
              displayEmpty
            >
              <MenuItem value="all">
                <Typography sx={{ fontFamily: 'Poppins' }}>
                  {t('charts.applications.all_posts')}
                </Typography>
              </MenuItem>
              {availablePosts.map((post) => (
                <MenuItem key={post.label} value={post.label}>
                  <Typography sx={{ fontFamily: 'Poppins' }}>
                    {post.label}
                  </Typography>
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>
        </Box>
      </CardHeaderWrapper>
      <CardBodyWrapper>
        <Box sx={{ 
          height: { xs: 250, sm: 300, md: 350 },
          width: '100%',
          position: 'relative',
          '& canvas': {
            maxWidth: '100% !important',
            height: '90% !important'
          }
        }}>
          <Chart
            type="bar"
            data={filteredData}
            options={{
              ...chartOptions,
              maintainAspectRatio: false,
              responsive: true,
            }}
          />
        </Box>
      </CardBodyWrapper>
    </StyledCard>
  );
}

export default ApplicationCadidatesChart;